import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Download } from "lucide-react";
import { adminLeads, updateLeadStatus, downloadCsv, zar } from "@/api";
import LeadDetailModal from "@/components/admin/LeadDetailModal";

const TYPE_META = {
  application: { label: "Application", cls: "bg-[#F57C00]/15 text-[#F57C00] border-[#F57C00]/40" },
  prequalification: { label: "Pre-qual", cls: "bg-sky-400/10 text-sky-300 border-sky-300/40" },
  rental_booking: { label: "Rental", cls: "bg-indigo-400/10 text-indigo-300 border-indigo-400/40" },
  trade_in: { label: "Trade-in", cls: "bg-amber-400/10 text-amber-300 border-amber-400/40" },
  contact: { label: "Enquiry", cls: "bg-emerald-400/10 text-emerald-300 border-emerald-400/40" },
};

const FILTERS = [
  { key: "", label: "All" },
  { key: "application", label: "Applications" },
  { key: "prequalification", label: "Pre-qualifications" },
  { key: "rental_booking", label: "Rentals" },
  { key: "trade_in", label: "Trade-ins" },
  { key: "contact", label: "Enquiries" },
];

const STATUSES = ["new", "contacted", "in-progress", "closed"];

const summary = (lead) => {
  if (lead.type === "application") {
    const v = lead.payload?.vehicle || {};
    return `${lead.reference || ""} · ${[v.make, v.model].filter(Boolean).join(" ") || "Any vehicle"} · ${v.term || 48} months`;
  }
  if (lead.type === "prequalification") {
    return `${lead.result_status === "pre_qualified" ? "Pre-qualified" : lead.result_status === "likely_review" ? "Needs review" : "Not eligible now"} · surplus ${zar(lead.surplus)}${lead.vehicle_interest ? ` · ${lead.vehicle_interest}` : ""}`;
  }
  if (lead.type === "rental_booking") {
    return `${lead.reference || ""} · ${lead.vehicle_name || "Any vehicle"} · ${lead.start_date} → ${lead.end_date} (${lead.days}d)${lead.estimated_total ? ` · est ${zar(lead.estimated_total)}` : ""}`;
  }
  if (lead.type === "trade_in") {
    return `${lead.reference || ""} · ${lead.current_vehicle} · ${lead.condition}${lead.desired_vehicle ? ` → wants ${lead.desired_vehicle}` : ""}`;
  }
  return lead.message?.length > 90 ? `${lead.message.slice(0, 90)}…` : lead.message || "";
};

export default function LeadsTab() {
  const [leads, setLeads] = useState([]);
  const [filter, setFilter] = useState("");
  const [loading, setLoading] = useState(true);
  const [detailId, setDetailId] = useState(null);

  const load = useCallback((type) => {
    setLoading(true);
    adminLeads(type)
      .then((d) => setLeads(d.leads))
      .catch(() => toast.error("Could not load leads"))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => load(filter), [filter, load]);

  const changeStatus = async (lead, status) => {
    try {
      await updateLeadStatus(lead.id, status);
      setLeads((ls) => ls.map((l) => (l.id === lead.id ? { ...l, status } : l)));
      toast.success("Status updated");
    } catch {
      toast.error("Could not update status");
    }
  };

  return (
    <div data-testid="leads-tab">
      <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
        <div className="flex flex-wrap gap-3" data-testid="lead-filters">
          {FILTERS.map((f) => (
            <button
              key={f.key}
              data-testid={`lead-filter-${f.key || "all"}`}
              onClick={() => setFilter(f.key)}
              className={`text-sm font-bold px-4 py-2.5 rounded-full border transition-colors duration-200 ${
                filter === f.key ? "bg-[#F57C00] border-[#F57C00] text-white" : "border-sky-300/30 text-slate-300 hover:border-sky-300"
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>
        <div className="flex gap-2">
          <button
            data-testid="export-leads-csv"
            onClick={() => downloadCsv("leads.csv", "compcar-leads.csv").catch(() => toast.error("Export failed"))}
            className="inline-flex items-center gap-2 border border-sky-300/30 text-slate-300 hover:text-white hover:border-sky-300 text-xs font-bold px-4 py-2.5 rounded-md transition-colors"
          >
            <Download size={14} /> Leads CSV
          </button>
          <button
            data-testid="export-payments-csv"
            onClick={() => downloadCsv("payments.csv", "compcar-payments.csv").catch(() => toast.error("Export failed"))}
            className="inline-flex items-center gap-2 border border-sky-300/30 text-slate-300 hover:text-white hover:border-sky-300 text-xs font-bold px-4 py-2.5 rounded-md transition-colors"
          >
            <Download size={14} /> Payments CSV
          </button>
        </div>
      </div>

      <div className="bg-[#122659] border border-sky-300/20 rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="leads-table">
            <thead>
              <tr className="text-left text-[10px] uppercase tracking-widest text-sky-300 border-b border-sky-300/10">
                <th className="px-6 py-4">Received</th>
                <th className="px-6 py-4">Type</th>
                <th className="px-6 py-4">Contact</th>
                <th className="px-6 py-4">Summary</th>
                <th className="px-6 py-4">Status</th>
              </tr>
            </thead>
            <tbody>
              {leads.map((lead) => {
                const meta = TYPE_META[lead.type] || TYPE_META.contact;
                return (
                  <tr key={lead.id} data-testid={`lead-row-${lead.id}`} onClick={() => setDetailId(lead.id)} className="border-b border-white/5 hover:bg-white/[0.05] transition-colors cursor-pointer">
                    <td className="px-6 py-4 text-slate-400 whitespace-nowrap">
                      {new Date(lead.created_at).toLocaleDateString("en-ZA", { day: "numeric", month: "short" })}
                      <span className="block text-xs text-slate-600">
                        {new Date(lead.created_at).toLocaleTimeString("en-ZA", { hour: "2-digit", minute: "2-digit" })}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`text-[10px] uppercase tracking-wider font-bold border rounded-full px-2.5 py-1 ${meta.cls}`}>
                        {meta.label}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <p className="font-bold text-white">{lead.full_name || lead.name}</p>
                      {lead.payment_due && (
                        <span data-testid={`lead-overdue-${lead.id}`} className="inline-block mt-1 text-[10px] uppercase tracking-wider font-bold text-red-300 bg-red-400/15 border border-red-400/40 rounded-full px-2.5 py-0.5">
                          Payment overdue
                        </span>
                      )}
                      <p className="text-xs text-slate-500">{lead.mobile || lead.phone || ""}</p>
                      <p className="text-xs text-slate-500">{lead.email}</p>
                    </td>
                    <td className="px-6 py-4 text-slate-300 max-w-xs">{summary(lead)}</td>
                    <td className="px-6 py-4">
                      <select
                        data-testid={`lead-status-${lead.id}`}
                        value={lead.status}
                        onClick={(e) => e.stopPropagation()}
                        onChange={(e) => changeStatus(lead, e.target.value)}
                        className="bg-[#0B1F4E] border border-sky-300/30 text-slate-200 text-xs font-semibold rounded-md px-3 py-2 outline-none focus:border-sky-300"
                      >
                        {STATUSES.map((s) => (
                          <option key={s} value={s}>{s[0].toUpperCase() + s.slice(1)}</option>
                        ))}
                      </select>
                    </td>
                  </tr>
                );
              })}
              {!loading && leads.length === 0 && (
                <tr>
                  <td colSpan={5} className="px-6 py-12 text-center text-slate-400" data-testid="leads-empty-row">
                    No leads in this category yet.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
      {loading && <p className="text-slate-500 text-sm mt-4" data-testid="leads-loading">Loading leads…</p>}
      {detailId && <LeadDetailModal leadId={detailId} onClose={() => setDetailId(null)} />}
    </div>
  );
}
